import React, { useEffect, useMemo, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";
import { leaderboardSourceCatalog } from "./leaderboardSourceCatalog";

const defaultFilters = {
  game: "all",
  mode: "all",
  season: "2026-Q1",
};

function flagFromCountry(code) {
  if (!code || code.length !== 2) return "🏳️";
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt()));
}

export default function LeaderboardsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!hasSupabaseConfig || !supabase) {
        setMessage("Falta configuración de Supabase para cargar leaderboards.");
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("leaderboard_entries")
          .select("id,season,game_title,mode,rank_position,display_name,platform,country_code,is_puerto_rico,points,wins,losses,source,last_synced_at")
          .eq("is_puerto_rico", true)
          .order("game_title", { ascending: true })
          .order("rank_position", { ascending: true })
          .limit(2000);

        if (error) throw error;
        if (!cancelled) setRows(data || []);
      } catch (error) {
        if (!cancelled) setMessage(`No se pudo cargar leaderboard: ${error.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const games = useMemo(() => {
    const set = new Set(rows.map((r) => r.game_title).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [rows]);

  const modes = useMemo(() => {
    const set = new Set(rows.map((r) => r.mode).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (filters.game !== "all" && row.game_title !== filters.game) return false;
      if (filters.mode !== "all" && row.mode !== filters.mode) return false;
      if (filters.season && row.season !== filters.season) return false;
      return true;
    });
  }, [rows, filters]);

  return (
    <div className="bg-[#06070A] text-[#F5F7FF] min-h-screen">
      <SiteHeader current="/leaderboards" />

      <section className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="rounded-2xl border border-cyan-300/30 bg-gradient-to-r from-cyan-500/10 to-violet-500/10 p-7 md:p-10">
          <p className="text-cyan-300 tracking-[0.2em] uppercase text-xs">PREL · Leaderboards</p>
          <h1 className="text-3xl md:text-5xl font-bold mt-3">Puerto Rico Competitive Rankings</h1>
          <p className="text-slate-200 mt-4 max-w-4xl">
            Ranking automatizado para jugadores y equipos vinculados a Puerto Rico.
            Esta versión combina datos sincronizados por fuentes externas + validación interna PREL.
          </p>
          <p className="text-slate-400 mt-2 text-sm">
            Nota: algunas plataformas (ej. Epic/Fortnite) requieren integración autorizada para datos completos.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-8">
        <div className="rounded-2xl border border-white/10 bg-[#111722] p-5">
          <h2 className="text-xl font-semibold">Fuentes de datos (TOS-safe)</h2>
          <p className="text-sm text-slate-300 mt-1">Top 10 juegos populares PR con fuente recomendada. Solo usaremos fuentes oficiales/públicas permitidas o partner access.</p>
          <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm">
            {leaderboardSourceCatalog.map((item) => (
              <a key={item.game} href={item.url} target="_blank" rel="noreferrer" className="rounded-lg border border-white/10 p-3 hover:bg-white/5">
                <p className="font-semibold">{item.game}</p>
                <p className="text-slate-300">{item.source}</p>
                <p className="text-xs text-slate-400 mt-1">{item.notes}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-6 grid md:grid-cols-3 gap-3">
        <select
          value={filters.game}
          onChange={(e) => setFilters((f) => ({ ...f, game: e.target.value }))}
          className="rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
        >
          {games.map((g) => <option key={g} value={g}>{g === "all" ? "Todos los juegos" : g}</option>)}
        </select>

        <select
          value={filters.mode}
          onChange={(e) => setFilters((f) => ({ ...f, mode: e.target.value }))}
          className="rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
        >
          {modes.map((m) => <option key={m} value={m}>{m === "all" ? "Todos los modos" : m}</option>)}
        </select>

        <input
          value={filters.season}
          onChange={(e) => setFilters((f) => ({ ...f, season: e.target.value }))}
          placeholder="Temporada (ej. 2026-Q1)"
          className="rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
        />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-20">
        {message ? <p className="text-sm text-slate-300 mb-3">{message}</p> : null}

        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0f1420] text-slate-300">
                <tr>
                  <th className="text-left px-4 py-3">Rank</th>
                  <th className="text-left px-4 py-3">Player/Team</th>
                  <th className="text-left px-4 py-3">Juego</th>
                  <th className="text-left px-4 py-3">Modo</th>
                  <th className="text-left px-4 py-3">Pts</th>
                  <th className="text-left px-4 py-3">W-L</th>
                  <th className="text-left px-4 py-3">Bandera</th>
                  <th className="text-left px-4 py-3">Fuente</th>
                  <th className="text-left px-4 py-3">Sync</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-slate-300">Cargando leaderboard...</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-slate-300">No hay resultados con esos filtros.</td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <tr key={row.id} className="border-t border-white/10">
                      <td className="px-4 py-3 font-semibold">#{row.rank_position}</td>
                      <td className="px-4 py-3">{row.display_name}</td>
                      <td className="px-4 py-3">{row.game_title}</td>
                      <td className="px-4 py-3">{row.mode || "—"}</td>
                      <td className="px-4 py-3">{row.points ?? "—"}</td>
                      <td className="px-4 py-3">{row.wins ?? 0}-{row.losses ?? 0}</td>
                      <td className="px-4 py-3">{flagFromCountry(row.country_code)} {row.country_code || "—"}</td>
                      <td className="px-4 py-3">{row.source || "manual"}</td>
                      <td className="px-4 py-3 text-slate-400">{row.last_synced_at ? new Date(row.last_synced_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
